import axios from 'axios';
import { toast } from 'react-toastify';

// Base URL for API
const API_URL = 'https://nmdiamond.in/api';

// Helper function to ensure valid product ID
const getValidProductId = (productId) => {
  // If it's already a valid ID, return it
  if (productId && (typeof productId === 'string' || typeof productId === 'number')) {
    return productId;
  }
  
  // If it's an object with an id property, return that
  if (productId && typeof productId === 'object' && productId.id) {
    return productId.id;
  }
  
  // If it's an object with a product_id property, return that
  if (productId && typeof productId === 'object' && productId.product_id) {
    return productId.product_id;
  }
  
  console.error('Invalid product ID:', productId);
  throw new Error('Invalid product ID');
};

// Process product data to ensure consistent format
const processProductData = (product) => {
  if (!product) return null;
  
  // Ensure image path is correct
  let imagePath = product.image;
  if (imagePath) {
    if (!imagePath.startsWith('http') && !imagePath.startsWith('/')) {
      imagePath = '/' + imagePath;
    }
  } else {
    imagePath = '/media/placeholder.jpeg';
  }
  
  return {
    ...product,
    id: product.product_id || product.id,
    name: product.name || 'Product Name',
    price: product.price || 'Request for price',
    image: imagePath,
    category: product.category || 'uncategorized',
    featured: Boolean(product.featured),
    inStock: Boolean(product.inStock)
  };
};

// Get wishlist items
export const getWishlistItems = async () => {
  try {
    const response = await axios.get(`${API_URL}/wishlist/index.php`);
    console.log('Wishlist items response:', response.data);
    if (response.data.success) {
      // Process each item to ensure consistent format
      const items = (response.data.items || []).map(processProductData).filter(item => {
        // Accept all products, including those with "Product direct" names
        if (!item) {
          console.warn('Filtering out null product');
          return false;
        }
        return true;
      });
      
      console.log('Processed wishlist items:', items);
      return items;
    }
    console.error('API Error:', response.data);
    return [];
  } catch (error) {
    console.error('Error fetching wishlist:', error.response?.data || error.message);
    toast.error('Failed to load wishlist items');
    return [];
  }
};

// Add item to wishlist
export const addToWishlist = async (productOrId) => {
  try {
    const validProductId = getValidProductId(productOrId);
    console.log('Adding product to wishlist:', validProductId);

    // First, ensure the product exists in the database
    try {
      // If it's a full product object, we might need to add it to the database first
      if (typeof productOrId === 'object' && productOrId.name) {
        // This is a simplified approach - in a real app, you'd call a proper API
        console.log('Ensuring product exists in database:', productOrId);
      }
    } catch (error) {
      console.error('Error ensuring product exists:', error);
    }

    const response = await axios.post(`${API_URL}/wishlist/index.php`, {
      product_id: validProductId
    });

    console.log('Add to wishlist response:', response.data);

    if (response.data.success) {
      toast.success('Item added to wishlist');
      return true;
    } else {
      console.error('API Error:', response.data);
      toast.info(response.data.message || 'Item already in wishlist');
      return false;
    }
  } catch (error) {
    console.error('Error adding to wishlist:', error.response?.data || error.message);
    toast.error(error.response?.data?.message || 'Failed to add item to wishlist');
    return false;
  }
};

// Remove item from wishlist
export const removeFromWishlist = async (productId) => {
  try {
    const validProductId = getValidProductId(productId);
    console.log('Removing product from wishlist:', validProductId);

    const response = await axios.delete(`${API_URL}/wishlist/index.php`, {
      data: { product_id: validProductId }
    });

    console.log('Remove from wishlist response:', response.data);

    if (response.data.success) {
      toast.success('Item removed from wishlist');
      return true;
    }
    console.error('API Error:', response.data);
    return false;
  } catch (error) {
    console.error('Error removing from wishlist:', error.response?.data || error.message);
    toast.error(error.response?.data?.message || 'Failed to remove item from wishlist');
    return false;
  }
};

// Check if item is in wishlist
export const isInWishlist = async (productId) => {
  try {
    const validProductId = getValidProductId(productId);
    const wishlistItems = await getWishlistItems();
    
    return Array.isArray(wishlistItems) && wishlistItems.some(item => {
      const itemProductId = item.product_id || item.id;
      return itemProductId == validProductId; // Use loose equality to compare string/number
    });
  } catch (error) {
    console.error('Error checking wishlist:', error.response?.data || error.message);
    return false;
  }
}; 